import axiosInstance from "../common/axiosInstance";
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_INTERACTION_ANALYSIS_FEEDBACK: "/interactions/analysis-feedback",
  UPSERT_INTERACTION_ANALYSIS_FEEDBACK: "/interactions/analysis-feedback/upsert",
  GET_INTERACTION_FEEDBACK_OPTIONS: "/interactions/feedback-options",
  GET_INTERACTION_FEEDBACK: "/interactions/feedback",
  UPSERT_INTERACTION_FEEDBACK: "/interactions/feedback",
});

interface GetInteractionAnalysisFeedbackParams {
  interactionId: string;
}

interface UpsertInteractionAnalysisFeedbackBody {
    id?: string;
    summaryByTopicFeedback?: string | null;
    overAllScoringFeedback: boolean | null;
    isForUpdate: boolean;
  }
interface UpsertInteractionFeedbackBody {
  feedbackReference: string;
  key: string;
  selectedFeedbackOptionId: string;
  additionalComments?: string | null;
}
const getInteractionAnalysisFeedback = async (
  params: GetInteractionAnalysisFeedbackParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_INTERACTION_ANALYSIS_FEEDBACK, {
    params: {
      interactionId: params.interactionId,
    },
  });
  return response;
};

const useGetInteractionAnalysisFeedback = (
  params: GetInteractionAnalysisFeedbackParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_interaction_analysis_feedback", stringifiedParams],
    queryFn: () => getInteractionAnalysisFeedback(params),
    enabled,
    retry: 0,
  });
};

const upsertInteractionAnalysisFeedback = async (
    interactionId: string,
    body: UpsertInteractionAnalysisFeedbackBody
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `${PATHS.UPSERT_INTERACTION_ANALYSIS_FEEDBACK}?interactionId=${interactionId}`,
      body
    );
    return response;
  };

  const useUpsertInteractionAnalysisFeedback = (): UseMutationResult<any, unknown, {
    interactionId: string;
    body: UpsertInteractionAnalysisFeedbackBody;
  }> => {
    return useMutation({
      mutationFn: ({ interactionId, body }) =>
        upsertInteractionAnalysisFeedback(interactionId, body),
    });
  };
  
  const getInteractionFeedbackOptions = async (): Promise<any> => {
    const response = await axiosInstance.get(PATHS.GET_INTERACTION_FEEDBACK_OPTIONS);
    return response;
  };

  const useGetInteractionFeedbackOptions = (
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_interaction_feedback_options"],
      queryFn: () => getInteractionFeedbackOptions(),
      enabled,
      retry: 0,
    });
  };

  const getInteractionFeedback = async (
    params: GetInteractionAnalysisFeedbackParams
  ): Promise<any> => {
    const response = await axiosInstance.get(PATHS.GET_INTERACTION_FEEDBACK, {
      params: {
        interactionId: params.interactionId,
      },
    });
    return response;
  };

  const useGetInteractionFeedback = (
    params: GetInteractionAnalysisFeedbackParams,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    const stringifiedParams = JSON.stringify(params);

    return useQuery({
      queryKey: ["get_interaction_feedback", stringifiedParams],
      queryFn: () => getInteractionFeedback(params),
      enabled,
      retry: 0,
    });
  };

  const upsertInteractionFeedback = async (
    interactionId: string,
    body: UpsertInteractionFeedbackBody
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `${PATHS.UPSERT_INTERACTION_FEEDBACK}?interactionId=${interactionId}`,
      body
    );

    return response;
  };

  const useUpsertInteractionFeedback = (): UseMutationResult<
    any,
    unknown,
    { interactionId: string; body: UpsertInteractionFeedbackBody }
  > => {
    return useMutation({
      mutationFn: ({ interactionId, body }) =>
        upsertInteractionFeedback(interactionId, body),
    });
  };
export interface InteractionFeedbackService {
  readonly useGetInteractionAnalysisFeedback: (
    params: GetInteractionAnalysisFeedbackParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpsertInteractionAnalysisFeedback: () => UseMutationResult<
    any,
    unknown,
    { interactionId: string; body: UpsertInteractionAnalysisFeedbackBody }
  >;

  readonly useGetInteractionFeedbackOptions: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetInteractionFeedback: (
    params: GetInteractionAnalysisFeedbackParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpsertInteractionFeedback: () => UseMutationResult<
    any,
    unknown,
    { interactionId: string; body: UpsertInteractionFeedbackBody }
  >;
}

const InteractionFeedbackService: InteractionFeedbackService = Object.freeze({
  useGetInteractionAnalysisFeedback,
  useUpsertInteractionAnalysisFeedback,
  useGetInteractionFeedbackOptions,
  useGetInteractionFeedback,
  useUpsertInteractionFeedback,
});

export default InteractionFeedbackService;
